import Service from '@ember/service';
import {
  sub,
  eachDayOfInterval,
  add,
  max,
  min,
  startOfDay as pureStartOfDay
} from 'date-fns';
import { inject as service } from '@ember/service';
import { task, timeout } from 'ember-concurrency';
import { isoDate } from '@dubbletrack/time/helpers/iso-date';
import _, { sortBy } from 'lodash';
import { tracked } from '@glimmer/tracking';
import { registerDestructor } from '@ember/destroyable';
import startOfDay from "@dubbletrack/time/utils/start-of-day";
import endOfDay from "@dubbletrack/time/utils/end-of-day";

class ScheduleDay {
  @tracked airings = [];
  @tracked isLoaded = false;

  constructor(date) {
    this.id = isoDate(date, { dateOnly: true });
    this.date = date;
  }
}

export default class ScheduleDataStoreService extends Service {
  @service dataStore;
  @service store;
  @service socket;
  @service settings;
  @service clock;

  maxTime = endOfDay(add(new Date(), { weeks: 1 }));
  minTime = sub(new Date(), { weeks: 2 });
  loadingDays = new Map();
  @tracked dates;
  @tracked dateObjects;
  @tracked _currentItem;

  constructor() {
    super(...arguments);
    this.dates = eachDayOfInterval({
      start: this.minTime,
      end: this.maxTime,
    }).map((a) => pureStartOfDay(a));


    this.dateObjects = this.dates.map((date) => new ScheduleDay(date));

    registerDestructor(this, () => {
      this.subscription?.unsubscribe();
      this.subscription = undefined;
    });
  }

  get currentItem() {
    if (this._currentItem) {
      return this._currentItem;
    }

    return this.dateObjects.find(
      (obj) => obj.id == isoDate(pureStartOfDay(new Date()), { dateOnly: true }),
    )
  }
  set currentItem(value) {
    this._currentItem = value;
  }

  loadAiringsFromDateTask = task(
    { maxConcurrency: 10, restartable: true },
    async (dateObject, reload = false) => {
      if (dateObject.isLoaded && !reload) {
        return;
      }

      // A day scrolling into view asks several times before its first load lands,
      // and a schedule change signals once per airing it touched.
      let loading = this.loadingDays.get(dateObject.id);
      if (loading && !reload) {
        return loading;
      }
      loading?.cancel();

      loading = this.dataStore.loadAiringsTask.perform(
        startOfDay(dateObject.date, { timeZone: this.clock.timeZone }),
        endOfDay(dateObject.date, { timeZone: this.clock.timeZone }),
      );
      this.loadingDays.set(dateObject.id, loading);
      try {
        // The API leaves out an airing a temporary show has replaced, so the day is what came back, not the store.
        dateObject.airings = sortBy(await loading, ['startsAt']);
        dateObject.isLoaded = true;
      } finally {
        if (this.loadingDays.get(dateObject.id) === loading) {
          this.loadingDays.delete(dateObject.id);
        }
      }
      this.loadCableTask.perform();
    }
  );

  loadCableTask = task({ maxConcurrency: 1, restartable: true }, async () => {
    await timeout(300);

    let relevantDateObjects = this.dateObjects.filter((dateObj) => dateObj.isLoaded);

    let earliestDate = min(relevantDateObjects.map((item) => startOfDay(item.date, { timeZone: this.clock.timeZone })));
    let latestDate = max(relevantDateObjects.map((item) => endOfDay(item.date, { timeZone: this.clock.timeZone })));

    // Every reload comes back through here, and resubscribing to the same range would drop signals in the gap.
    let range = `${earliestDate.toISOString()}/${latestDate.toISOString()}`;
    if (this.subscription && range === this.subscribedRange) return;
    this.subscribedRange = range;

    this.subscription?.unsubscribe();
    this.subscription = this.socket.subscribe(
      {
        channel: 'SignalChannel',
        station_id: this.settings.stationId,
        iso_start_date: earliestDate.toISOString(),
        iso_end_date: latestDate.toISOString(),
      },
      {
        received: async (payload) => {
          if (this.settings.debug) {
            console.log(`⚡️ schedule day received cable update`, payload);
          }

          // The API says whether the airing alone will do or the day has to go again,
          // since a change of shape reaches the airings it overlaps without a signal.
          if (payload.directive === 'reload_airing' && payload.source?.id) {
            this.dataStore.loadAiringTask.perform(payload.source.id);
            return;
          }

          let dateObject = [...this.dateObjects].find(
            (dateObj) => dateObj.id == payload.date,
          );
          if (dateObject) {
            this.loadAiringsFromDateTask.perform(dateObject, true);
          }
        },
      },
    );
  });

}
